import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  FormField,
  Input,
  PrimaryButton,
  RadioGroup,
  SecondaryButton,
  DangerButton,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { useDeactivateGlobalDiscount, useGlobalDiscount, useUpsertGlobalDiscount } from "../hooks/useAdminGlobalDiscount";

const DEFAULT_CURRENCY = "USD";

// Status color patterns (explicit dark variants allowed for semantic status states)
const noticeStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  warning: "bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
};

const toDateTimeLocalValue = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  // yyyy-MM-ddTHH:mm (local)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDateTimeLocalValueToIso = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatDiscountLabel = (d) => {
  if (!d) return "—";
  if (d.percent_off != null) return `${d.percent_off}% off`;
  if (d.amount_off != null) return `${d.amount_off} ${d.currency || DEFAULT_CURRENCY} off`;
  if ((d.free_trial_days || 0) > 0) return `${d.free_trial_days} trial days`;
  return "—";
};

const computeStatus = (d) => {
  if (!d) return { badge: "None", badgeTone: "neutral", subtitle: "No global discount configured." };
  const active = Boolean(d.is_effectively_active);
  const now = Date.now();
  const endsAt = d.ends_at ? new Date(d.ends_at).getTime() : null;
  const startsAt = d.starts_at ? new Date(d.starts_at).getTime() : null;

  let subtitle = active ? "Currently active." : "Not currently active.";
  if (startsAt && startsAt > now) subtitle = `Scheduled to start in ${Math.max(0, Math.round((startsAt - now) / 3600000))}h.`;
  if (endsAt && endsAt > now) subtitle = `${subtitle} Ends in ${Math.max(0, Math.round((endsAt - now) / 3600000))}h.`;

  return {
    badge: active ? "Active" : "Inactive",
    badgeTone: active ? "success" : "neutral",
    subtitle,
  };
};

const GlobalDiscountAdminPage = () => {
  const { data, isLoading, error } = useGlobalDiscount();
  const upsertMutation = useUpsertGlobalDiscount();
  const deactivateMutation = useDeactivateGlobalDiscount();

  const [notice, setNotice] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const initial = useMemo(() => {
    const d = data || null;
    const discountType =
      d?.percent_off != null ? "percent" : d?.amount_off != null ? "amount" : (d?.free_trial_days || 0) > 0 ? "trial" : "percent";
    return {
      name: d?.name || "",
      discountType,
      percent_off: d?.percent_off != null ? String(d.percent_off) : "",
      amount_off: d?.amount_off != null ? String(d.amount_off) : "",
      currency: d?.currency || DEFAULT_CURRENCY,
      include_renewals: Boolean(d?.include_renewals),
      free_trial_days: d?.free_trial_days != null ? String(d.free_trial_days) : "0",
      starts_at: toDateTimeLocalValue(d?.starts_at),
      ends_at: toDateTimeLocalValue(d?.ends_at),
      max_redemptions: d?.max_redemptions != null ? String(d.max_redemptions) : "",
      first_time_only: Boolean(d?.first_time_only),
      plans: Array.isArray(d?.plans) ? d.plans : [],
      billing_periods: Array.isArray(d?.billing_periods) ? d.billing_periods : [],
      active: d?.active != null ? Boolean(d.active) : true,
    };
  }, [data]);

  const [form, setForm] = useState(initial);

  // Keep form in sync when first loaded / refetched
  useEffect(() => setForm(initial), [initial]);

  const status = computeStatus(data);
  const statusBadgeClass =
    status.badgeTone === "success"
      ? noticeStyles.success
      : `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`;

  const toggleArrayValue = (arr, value) => {
    const next = new Set(arr || []);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return Array.from(next);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required.";

    if (form.discountType === "percent") {
      const v = Number(form.percent_off);
      if (!form.percent_off) errors.percent_off = "percent_off is required.";
      else if (Number.isNaN(v) || v <= 0 || v > 100) errors.percent_off = "Must be between 0 and 100.";
    }
    if (form.discountType === "amount") {
      const v = Number(form.amount_off);
      if (!form.amount_off) errors.amount_off = "amount_off is required.";
      else if (Number.isNaN(v) || v <= 0) errors.amount_off = "Must be greater than 0.";
    }
    if (form.discountType === "trial") {
      const v = Number(form.free_trial_days);
      if (Number.isNaN(v) || v <= 0) errors.free_trial_days = "Trial days must be > 0.";
    }

    if (form.starts_at && form.ends_at) {
      const s = new Date(form.starts_at).getTime();
      const e = new Date(form.ends_at).getTime();
      if (!Number.isNaN(s) && !Number.isNaN(e) && s >= e) errors.ends_at = "End must be after start.";
    }

    if (form.max_redemptions) {
      const m = Number(form.max_redemptions);
      if (Number.isNaN(m) || m <= 0) errors.max_redemptions = "Must be > 0.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      include_renewals: Boolean(form.include_renewals),
      currency: (form.currency || DEFAULT_CURRENCY).toUpperCase(),
      free_trial_days: Number(form.free_trial_days || 0),
      starts_at: fromDateTimeLocalValueToIso(form.starts_at),
      ends_at: fromDateTimeLocalValueToIso(form.ends_at),
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      first_time_only: Boolean(form.first_time_only),
      plans: form.plans || [],
      billing_periods: form.billing_periods || [],
      active: Boolean(form.active),
      percent_off: null,
      amount_off: null,
    };

    // Backend requires exactly one discount type; set only one.
    if (form.discountType === "percent") payload.percent_off = Number(form.percent_off);
    if (form.discountType === "amount") payload.amount_off = Number(form.amount_off);
    if (form.discountType === "trial") payload.free_trial_days = Number(form.free_trial_days || 0);

    return payload;
  };

  const onSave = async () => {
    setNotice(null);
    if (!validateForm()) return;

    try {
      const res = await upsertMutation.mutateAsync(buildPayload());
      const warning = res?.warning || res?.stripe?.warning || (res?.stripe?.warnings || []).join("\n");
      setNotice(
        warning
          ? { type: "warning", message: `Saved. Stripe sync warning: ${warning}` }
          : { type: "success", message: "Saved global discount." }
      );
    } catch (e) {
      setNotice({ type: "error", message: e?.message || "Failed to save." });
      setFormErrors(e?.data || {});
    }
  };

  const onDeactivate = async () => {
    setNotice(null);
    try {
      const res = await deactivateMutation.mutateAsync();
      const warning = res?.warning || res?.stripe?.warning || (res?.stripe?.warnings || []).join("\n");
      setNotice(
        warning
          ? { type: "warning", message: `Deactivated. Stripe sync warning: ${warning}` }
          : { type: "success", message: "Deactivated global discount." }
      );
    } catch (e) {
      setNotice({ type: "error", message: e?.message || "Failed to deactivate." });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h2 className={`${typography.h3} ${textColors.primary}`}>Global Discount</h2>
          <p className={`${typography.body} ${textColors.secondary}`}>
            Configure a single global discount that applies automatically to everyone (and stacks with coupon codes).
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <SecondaryButton
            className="w-full sm:w-auto whitespace-nowrap"
            onClick={() => setForm(initial)}
            disabled={isLoading || upsertMutation.isPending}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton
            className="w-full sm:w-auto whitespace-nowrap"
            onClick={onSave}
            disabled={isLoading || upsertMutation.isPending}
          >
            Save Global Discount
          </PrimaryButton>
        </div>
      </div>

      {notice && (
        <div className="mt-4">
          <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type] || `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`}`}>
            <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <div className={`rounded-2xl px-4 py-3 ${noticeStyles.error}`}>
            <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{error?.message || "Failed to load."}</p>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status */}
        <Card className="lg:col-span-1">
          <CardHeader title="Current Status" />
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <div className={`${typography.h4} ${textColors.primary}`}>{data?.name || "No discount"}</div>
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>{formatDiscountLabel(data)}</div>
              </div>
              <div className={`${typography.bodySmall} ${textColors.primary}`}>
                <span className={`px-2 py-1 rounded ${statusBadgeClass}`}>
                  {status.badge}
                </span>
              </div>
            </div>

            <div className={`mt-3 ${typography.bodySmall} ${textColors.secondary}`}>{status.subtitle}</div>

            <Divider className="my-4" />

            <div className="space-y-2">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>Redemptions:</span>{" "}
                {data ? `${data.times_redeemed ?? 0}${data.max_redemptions != null ? ` / ${data.max_redemptions}` : ""}` : "—"}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>First-time only:</span> {data ? (data.first_time_only ? "Yes" : "No") : "—"}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>Schedule:</span>{" "}
                {data?.starts_at ? new Date(data.starts_at).toLocaleString() : "Immediate"} →{" "}
                {data?.ends_at ? new Date(data.ends_at).toLocaleString() : "No end"}
              </div>
            </div>

            <Divider className="my-4" />

            <DangerButton onClick={onDeactivate} disabled={!data?.is_effectively_active || deactivateMutation.isPending}>
              Deactivate Now
            </DangerButton>
          </CardBody>
        </Card>

        {/* Editor */}
        <Card className="lg:col-span-2">
          <CardHeader title="Edit / Create" />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Name" htmlFor="global_discount_name" error={formErrors.name}>
                <Input
                  id="global_discount_name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Holiday Sale"
                />
              </FormField>

              <FormField label="Active">
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
                  <span className={`${typography.bodySmall} ${textColors.secondary}`}>Enable discount</span>
                </div>
              </FormField>
            </div>

            <Divider className="my-6" />

            <FormField label="Discount Type">
              <RadioGroup
                value={form.discountType}
                onChange={(v) =>
                  setForm((p) => ({
                    ...p,
                    discountType: v,
                    percent_off: v === "percent" ? p.percent_off : "",
                    amount_off: v === "amount" ? p.amount_off : "",
                    free_trial_days: v === "trial" ? (p.free_trial_days && p.free_trial_days !== "0" ? p.free_trial_days : "14") : p.free_trial_days,
                  }))
                }
                options={[
                  { label: "Percent", value: "percent" },
                  { label: "Amount", value: "amount" },
                  { label: "Trial only", value: "trial" },
                ]}
              />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {form.discountType === "percent" && (
                <FormField label="Percent off" htmlFor="global_discount_percent_off" error={formErrors.percent_off}>
                  <Input
                    id="global_discount_percent_off"
                    value={form.percent_off}
                    onChange={(e) => setForm((p) => ({ ...p, percent_off: e.target.value }))}
                    placeholder="20"
                  />
                </FormField>
              )}

              {form.discountType === "amount" && (
                <>
                  <FormField label="Amount off" error={formErrors.amount_off}>
                    <Input value={form.amount_off} onChange={(e) => setForm((p) => ({ ...p, amount_off: e.target.value }))} placeholder="5.00" />
                  </FormField>
                  <FormField label="Currency">
                    <Input value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))} placeholder="USD" />
                  </FormField>
                </>
              )}

              <FormField label="Free trial days" error={formErrors.free_trial_days}>
                <Input
                  value={form.free_trial_days}
                  onChange={(e) => setForm((p) => ({ ...p, free_trial_days: e.target.value }))}
                  placeholder="0"
                />
              </FormField>
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={form.include_renewals} onChange={(e) => setForm((p) => ({ ...p, include_renewals: e.target.checked }))} />
                <span className={`${typography.bodySmall} ${textColors.secondary}`}>Apply to renewals</span>
              </div>
            </div>

            <Divider className="my-6" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Start datetime (optional)">
                <Input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm((p) => ({ ...p, starts_at: e.target.value }))}
                />
              </FormField>
              <FormField label="End datetime (optional)" error={formErrors.ends_at}>
                <Input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm((p) => ({ ...p, ends_at: e.target.value }))}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <FormField label="Max redemptions (optional)" error={formErrors.max_redemptions}>
                <Input value={form.max_redemptions} onChange={(e) => setForm((p) => ({ ...p, max_redemptions: e.target.value }))} placeholder="100" />
              </FormField>
              <FormField label="Eligibility">
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.first_time_only} onChange={(e) => setForm((p) => ({ ...p, first_time_only: e.target.checked }))} />
                  <span className={`${typography.bodySmall} ${textColors.secondary}`}>First-time subscribers only</span>
                </div>
              </FormField>
            </div>

            <Divider className="my-6" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className={`${typography.bodySmall} ${textColors.primary} mb-2`}>Plans</div>
                <div className="flex flex-col gap-2">
                  {["plus", "pro"].map((p) => (
                    <label key={p} className="flex items-center gap-2">
                      <Checkbox checked={form.plans.includes(p)} onChange={() => setForm((s) => ({ ...s, plans: toggleArrayValue(s.plans, p) }))} />
                      <span className={`${typography.bodySmall} ${textColors.secondary}`}>{p}</span>
                    </label>
                  ))}
                </div>
                <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>Leave empty to allow all plans.</div>
              </div>
              <div>
                <div className={`${typography.bodySmall} ${textColors.primary} mb-2`}>Billing periods</div>
                <div className="flex flex-col gap-2">
                  {["monthly", "yearly"].map((b) => (
                    <label key={b} className="flex items-center gap-2">
                      <Checkbox checked={form.billing_periods.includes(b)} onChange={() => setForm((s) => ({ ...s, billing_periods: toggleArrayValue(s.billing_periods, b) }))} />
                      <span className={`${typography.bodySmall} ${textColors.secondary}`}>{b}</span>
                    </label>
                  ))}
                </div>
                <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>Leave empty to allow all billing periods.</div>
              </div>
            </div>

            <Divider className="my-6" />

            <Card variant="glass">
              <CardHeader title="Preview" />
              <CardBody>
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>Users will see:</div>
                <div className={`mt-2 p-3 rounded ${surfaces.glass} ${borders.glass} border`}>
                  <div className={`${typography.bodySmall} ${textColors.primary}`}>
                    {form.name.trim() ? form.name.trim() : "Global Promotion"} — {form.discountType === "percent"
                      ? `${form.percent_off || "0"}% off`
                      : form.discountType === "amount"
                      ? `${form.amount_off || "0"} ${form.currency || DEFAULT_CURRENCY} off`
                      : `${form.free_trial_days || "0"} free trial days`}
                  </div>
                  <div className={`${typography.bodySmall} ${textColors.secondary}`}>Applied automatically at checkout.</div>
                </div>
              </CardBody>
            </Card>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default GlobalDiscountAdminPage;


